import React, { useState, useMemo } from "react";
import TableHeader from "./Header";
import Pagination from "./Pagination";
import Search from "./Search";
import classes from "./Table.module.css";
import SecBTN from "../../../UI/SecBTN";
import moment from "moment/moment";

const DataTable = ({ data, onViewQuestions, onNext, onRefresh }) => {

  const [showEntries, setShowEntries] = useState(10);
  const [totalItems, setTotalItems] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [search, setSearch] = useState("");

  // ✅ Default descending by Upload ID
  const [sorting, setSorting] = useState({
    field: "upload_id",
    order: "desc",
  });

  const ITEMS_PER_PAGE = Number(showEntries);

  const headers = [
    { name: "Upload ID", field: "pdf_upload_id", sortable: true },
    { name: "File Name", field: "path", sortable: true },
    { name: "Tab Name", field: "upload_file_type_name", sortable: true },
    { name: "Date", field: "datetime", sortable: true },
    { name: "Status", field: "status", sortable: true },
    { name: "Actions", field: "actions", sortable: false },
  ];

  const commentsData = useMemo(() => {
    let computed = data ? [...data] : [];

    // 🔍 SEARCH
    if (search) {
      const q = search.toLowerCase();
      computed = computed.filter((row) => {
        return (
          String(row?.pdf_upload_id).includes(q) ||
          row?.path?.toLowerCase().includes(q) ||
          row?.upload_file_type_name?.toLowerCase().includes(q) ||
          row?.status?.toLowerCase().includes(q)
        );
      });
    }

    setTotalItems(computed.length);

    // 🔃 SORTING
    if (sorting.field) {
      const reversed = sorting.order === "asc" ? 1 : -1;

      computed.sort((a, b) => {
        const av = a?.[sorting.field];
        const bv = b?.[sorting.field];

        // Date sorting
        if (!isNaN(Date.parse(av)) && !isNaN(Date.parse(bv))) {
          return reversed * (new Date(av) - new Date(bv));
        }

        // Number sorting
        if (!isNaN(av) && !isNaN(bv)) {
          return reversed * (av - bv);
        }

        // String sorting
        return reversed * String(av ?? "").localeCompare(String(bv ?? ""));
      });
    }

    // 📄 PAGINATION
    return computed.slice(
      (currentPage - 1) * ITEMS_PER_PAGE,
      currentPage * ITEMS_PER_PAGE
    );
  }, [data, search, sorting, currentPage, ITEMS_PER_PAGE]);

  return (
    <>
      {/* Top Controls */}
      <div className="d-flex justify-content-between align-items-center">
        <div>
          Show{" "}
          <select
            value={showEntries}
            onChange={(e) => {
              setShowEntries(e.target.value);
              setCurrentPage(1);
            }}
          >
            <option value="10">10</option>
            <option value="20">20</option>
            <option value="50">50</option>
          </select>{" "}
          entries
        </div>

        <div className="d-flex gap-2 align-items-center">
          {onRefresh && (
            <button
              className="btn btn-outline-danger btn-sm"
              onClick={onRefresh}
              title="Refresh data"
            >
              ↻ Refresh
            </button>
          )}
          <Search
            onSearch={(value) => {
              setSearch(value);
              setCurrentPage(1);
            }}
          />
        </div>
      </div>

      {/* Table */}
      <div className="table-responsive">
        <table className="table table-bordered mt-3 mb-0" style={{ tableLayout: "fixed", width: "100%" }}>
          <colgroup>
            <col style={{ width: "8%" }} /> {/* Upload ID */}
            <col style={{ width: "35%" }} /> {/* File Name */}
            <col style={{ width: "15%" }} /> {/* Tab Name */}
            <col style={{ width: "15%" }} /> {/* Date */}
            <col style={{ width: "10%" }} /> {/* Status */}
            <col style={{ width: "10%" }} /> {/* Actions */}
          </colgroup>
          <TableHeader
            headers={headers}
            sorting={sorting}
            onSorting={(field, order) =>
              setSorting({ field, order })
            }

          />

          <tbody>
            {commentsData.length > 0 ? (
              commentsData.map((row, index) => {

                // ✅ Status Label Mapping
                const statusMap = {
                  0: "Annotation",
                  1: "Extraction",
                  2: "Review",
                  3: "QID Generated",
                  9: "FIle Processing",
                  4: "Partially Extracted"
                };

                const statusLabel = statusMap[row.current_status] || "Unknown";

                // ✅ Button Click Handler
                const handleActionClick = () => {
                  // If status 0 or 1 → Step 2
                  console.log(row.current_status);
                  if (row.current_status === 0 || row.current_status === 1 || row.current_status === 4) {
                    onViewQuestions(row.upload_id, 2, row.current_status);
                  }
                  if (row.current_status === 2) {
                    onViewQuestions(row.upload_id, 3);
                  }

                  // If status 3 → Existing View Questions flow
                  if (row.current_status === 3) {
                    if (typeof onViewQuestions === "function") {
                      onViewQuestions(row.upload_id, 4);
                    }
                  }
                };

                return (
                  <tr key={row?.pdf_upload_id || index}>
                    <td>{row.upload_id}</td>

                    <td
                      style={{
                        width: "40%",
                        wordBreak: "break-word",
                        overflowWrap: "break-word",
                      }}
                    >
                      {row.file_name
                        ?.split("/")
                        .pop()
                        .replace(/_\d+_\d+(?=\.[^.]+$)/, "")}
                    </td>

                    <td>{row.upload_tab_name}</td>

                    <td>
                      {moment(row.uploaded_at).format("DD/MM/YYYY HH:mm")}

                    </td>

                    {/* ✅ Status Column */}
                    <td>{statusLabel}</td>

                    {/* ✅ Actions */}
                    <td>
                      {(row.current_status === 0 ||
                        row.current_status === 1 ||
                        row.current_status === 2 ||
                        row.current_status === 3 ||
                        row.current_status === 4) && (
                          <SecBTN
                            className="sec-btn1"
                            onClick={handleActionClick}
                          >
                            {row.current_status === 1 || row.current_status === 4
                              ? "View Progress"
                              : row.current_status === 2
                                ? "Review Questions"
                                : row.current_status === 3
                                  ? "Question List"
                                  : "View Annotation"}
                          </SecBTN>
                        )}
                    </td>
                  </tr>
                );
              })
            ) : (
              <tr>
                <td colSpan="6" className="text-center">
                  No Data Found
                </td>
              </tr>
            )}
          </tbody>

        </table>
      </div>

      {/* Footer */}
      <p className="small mt-2">
        Showing{" "}
        <b>
          {commentsData.length}
        </b>{" "}
        out of {totalItems} entries
      </p>

      <Pagination
        total={totalItems}
        itemsPerPage={ITEMS_PER_PAGE}
        currentPage={currentPage}
        onPageChange={(page) => setCurrentPage(page)}
      />
    </>
  );
};

export default DataTable;
